// src/components/MobileTacticsBoard.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Team, Player, FormationCell } from '../types';
import starPlayersData from '../data/star_players.json';
import './MobileTacticsBoard.css';

// 8 Position colors based on the approved mockup color map
const POSITION_COLORS: Record<string, string> = {
  'Lineman': '#64748b', // Slate Grey
  'Blitzer': '#dc2626', // Crimson Red
  'Thrower': '#2563eb', // Sapphire Blue
  'Catcher': '#f97316', // Warm Orange
  'Runner': '#fbbf24',  // Sunny Yellow
  'Blocker': '#16a34a', // Emerald Green
  'Big Guy': '#7c3aed', // Royal Purple
  'Special': '#0d9488', // Premium Dark Teal
};

// 8 Position abbreviations
const POSITION_ABBREVS: Record<string, string> = {
  'Lineman': 'LM',
  'Blitzer': 'BL',
  'Thrower': 'TH',
  'Catcher': 'CA',
  'Runner': 'RN',
  'Blocker': 'BK',
  'Big Guy': 'BG',
  'Special': 'SP',
};

// Parse position names to find matching archetype in parentheses or text
function getArchetype(position: string): string {
  const norm = position.toLowerCase();
  if (norm.includes('lineman')) return 'Lineman';
  if (norm.includes('blitzer')) return 'Blitzer';
  if (norm.includes('thrower')) return 'Thrower';
  if (norm.includes('catcher')) return 'Catcher';
  if (norm.includes('runner')) return 'Runner';
  if (norm.includes('blocker')) return 'Blocker';
  if (
    norm.includes('big guy') || 
    norm.includes('troll') || 
    norm.includes('ogre') || 
    norm.includes('minotaur') || 
    norm.includes('treeman') || 
    norm.includes('kroxigor') || 
    norm.includes('yhetee') || 
    norm.includes('mummy')
  ) {
    return 'Big Guy';
  }
  return 'Special';
}

const COLUMNS = Array.from({ length: 15 }, (_, i) => String.fromCharCode(65 + i));
const ROWS = Array.from({ length: 26 }, (_, i) => i + 1);

interface MobileTacticsBoardProps {
  team: Team;
  layout: FormationCell[];
  onChange: (updatedLayout: FormationCell[]) => void;
  selectedId: number | null;
  onSelect: (id: number | null) => void;
  isDesktop?: boolean;
}

export default function MobileTacticsBoard({
  team,
  layout,
  onChange,
  selectedId,
  onSelect,
  isDesktop
}: MobileTacticsBoardProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const [isBirdsEye, setIsBirdsEye] = useState(false);
  const [positionals, setPositionals] = useState<any[]>([]);
  const [pitchTheme, setPitchTheme] = useState<'grass' | 'astrogranite'>(() => {
    return (localStorage.getItem('bb-pitch-theme') as 'grass' | 'astrogranite') || 'grass';
  });

  // 1. Dynamically load active faction positional stats (MA/ST/AG/PA/AR/Skills)
  useEffect(() => {
    if (team && team.region) {
      import(`../data/${team.region.replace(/\s+/g, '')}.json`)
        .then((data) => {
          setPositionals(data.default?.Positionals || data.Positionals || []);
        })
        .catch(() => setPositionals([]));
    }
  }, [team]);

  // 2. Smooth auto-scroll to focus on Home setup zone (physical rows 13-26) when Focus view is enabled or on mount
  useEffect(() => {
    if (!isBirdsEye && viewportRef.current) {
      const container = viewportRef.current;
      setTimeout(() => {
        const cellSize = isDesktop ? 44 : 32;
        // Align Scrimmage line perfectly at the top
        container.scrollTop = 12 * cellSize;
        // Calculate grid width mathematically to bypass browser paint race conditions
        const gridWidth = 15 * cellSize + 16; // 15 columns * cell size + 16px padding
        container.scrollLeft = (gridWidth - container.clientWidth) / 2;
      }, 100);
    }
  }, [isBirdsEye, isDesktop]);

  // Pre-calculate tackle zones coverage map
  const tackleZoneMap = useMemo(() => {
    const map: Record<string, number> = {};
    layout.forEach(({ row, col }) => {
      const rIdx = ROWS.indexOf(row);
      const cIdx = col - 1;
      // 8 surrounding squares for tackle zones
      for (let dr = -1; dr <= 1; dr++) {
        for (let dc = -1; dc <= 1; dc++) {
          if (dr === 0 && dc === 0) continue;
          const nr = rIdx + dr;
          const nc = cIdx + dc;
          if (nr >= 0 && nr < ROWS.length && nc >= 0 && nc < COLUMNS.length) {
            const key = `${ROWS[nr]}-${COLUMNS[nc]}`;
            map[key] = (map[key] || 0) + 1;
          }
        }
      }
    });
    return map;
  }, [layout]);

  // Merge team players with selected Star Players dynamically
  const allPlayers = useMemo(() => {
    const playersList = [...team.players];
    const selectedStarPlayers = team.selectedStarPlayers || {};
    
    let starIdx = 0;
    starPlayersData.forEach((sp: any) => {
      if (selectedStarPlayers[sp.name]) {
        playersList.push({
          number: 17 + starIdx,
          name: sp.name,
          position: `Star Player: ${sp.name}`,
          stats: {
            MA: sp.MA,
            ST: sp.ST,
            AG: sp.AG,
            PA: sp.PA,
            AR: sp.AR,
            Skills: Array.isArray(sp.Skills) ? sp.Skills.join(', ') : (sp.Skills || 'None'),
            Primary: sp.Primary || '',
            Secondary: sp.Secondary || '',
            Cost: sp.Cost ? `${(sp.Cost / 1000)}K` : '0K'
          }
        });
        starIdx++;
      }
    });
    return playersList;
  }, [team.players, team.selectedStarPlayers]);

  // Helper to find a player by number
  const getPlayerByNum = (num: number): Player | undefined => {
    return allPlayers.find((p) => Number(p.number) === Number(num));
  };

  // Helper to extract player stats or fetch from positional definition
  const getPlayerStats = (player: Player) => {
    if (player.stats) {
      return {
        MA: player.stats.MA,
        ST: player.stats.ST,
        AG: player.stats.AG,
        PA: player.stats.PA,
        AR: player.stats.AR,
        Skills: player.stats.Skills,
        Cost: player.stats.Cost
      };
    }
    // Match position archetype name
    const match = positionals.find(p => player.position.includes(p.Position) || p.Position.includes(player.position));
    if (match) {
      return {
        MA: match.MA,
        ST: match.ST,
        AG: match.AG,
        PA: match.PA,
        AR: match.AR,
        Skills: match.Skills.join(', '),
        Cost: match.Cost
      };
    }
    // Fallbacks
    return {
      MA: '6',
      ST: '3',
      AG: '3+',
      PA: '4+',
      AR: '8+',
      Skills: 'Dodge',
      Cost: '50K'
    };
  };

  // Tap Interaction Logic
  const handleBenchTap = (playerNum: number) => {
    const isPlaced = layout.some((c) => Number(c.playerNumber) === Number(playerNum));
    if (isPlaced) {
      // If already on grid, recall it to the bench
      onChange(layout.filter((c) => Number(c.playerNumber) !== Number(playerNum)));
      if (selectedId === playerNum) {
        onSelect(null);
      }
    } else {
      // Toggle select for placement
      onSelect(selectedId === playerNum ? null : playerNum);
    }
  };

  const handleCellTap = (row: number, colLetter: string) => {
    const colNum = COLUMNS.indexOf(colLetter) + 1;
    const existingPlayer = layout.find((c) => c.row === row && c.col === colNum);

    if (existingPlayer) {
      if (selectedId === existingPlayer.playerNumber) {
        // Second click on the active selected player recalls them
        onChange(layout.filter((c) => Number(c.playerNumber) !== Number(existingPlayer.playerNumber)));
        onSelect(null);
      } else {
        // First click selects the player and displays their card
        onSelect(existingPlayer.playerNumber);
      }
    } else if (selectedId !== null) {
      // Block placement on the opponent's half of the pitch (rows 1-13)
      if (row < 14) return;

      // Tap on empty cell places the currently selected player there
      const filtered = layout.filter((c) => Number(c.playerNumber) !== Number(selectedId));
      onChange([...filtered, { playerNumber: selectedId, row, col: colNum }]);
      onSelect(null);
    }
  };

  return (
    <div className="mobile-board-container">
      {/* Slim Header Controls inside production Tab context */}
      <header className="mobile-board-header">
        <span className="board-title">
          {selectedId !== null ? `Placing Player #${selectedId}` : 'Bench'}
        </span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button 
            className="zoom-toggle-btn"
            onClick={() => {
              const next = pitchTheme === 'grass' ? 'astrogranite' : 'grass';
              setPitchTheme(next);
              localStorage.setItem('bb-pitch-theme', next);
            }}
          >
            {pitchTheme === 'grass' ? '🪨 Astro' : '🌱 Grass'}
          </button>
          <button 
            className={`zoom-toggle-btn ${isBirdsEye ? 'active' : ''}`}
            onClick={() => setIsBirdsEye(!isBirdsEye)}
          >
            {isBirdsEye ? '🔍 Focus view' : '⊡ Overview'}
          </button>
        </div>
      </header>

      {/* Selected Player Floating Status Bar - positioned above the bench */}
      {selectedId !== null && (() => {
        const selPlayer = getPlayerByNum(selectedId);
        if (!selPlayer) return null;
        const stats = getPlayerStats(selPlayer);
        return (
          <div className="selected-player-status-bar" onClick={() => onSelect(null)} style={{ cursor: 'pointer' }}>
            <div className="player-meta">
              <span className="player-number-badge">#{selPlayer.number}</span>
              <div className="player-identity">
                <div className="player-name">{selPlayer.name || `Unnamed Player`}</div>
                <div className="player-position">{selPlayer.position}</div>
              </div>
              <div className="player-cost">{stats.Cost}</div>
            </div>
            <div className="player-stats-grid">
              <div className="stat-box"><span className="label">MA</span><span className="val">{stats.MA}</span></div>
              <div className="stat-box"><span className="label">ST</span><span className="val">{stats.ST}</span></div>
              <div className="stat-box"><span className="label">AG</span><span className="val">{stats.AG}</span></div>
              <div className="stat-box"><span className="label">PA</span><span className="val">{stats.PA}</span></div>
              <div className="stat-box"><span className="label">AR</span><span className="val">{stats.AR}</span></div>
            </div>
            <div className="player-skills">
              <span className="label">Skills:</span> {stats.Skills || 'None'}
            </div>
          </div>
        );
      })()}

      {/* Bench Strip relocated directly under the header controls */}
      <section className="bench-strip-container">

        <div className="bench-strip-scroll">
          {allPlayers
            .filter((p) => p.position) // Only show players with a valid drafted position
            .map((player) => {
              const isPlaced = layout.some((c) => Number(c.playerNumber) === Number(player.number));
              const archetype = getArchetype(player.position);
              const tokenColor = POSITION_COLORS[archetype] || POSITION_COLORS['Lineman'];
              const tokenAbbrev = POSITION_ABBREVS[archetype] || 'LM';

              return (
                <div
                  key={player.number}
                  className={`bench-item-wrapper ${isPlaced ? 'on-field' : ''}`}
                  onClick={() => handleBenchTap(player.number)}
                >
                  <div
                    className={`player-counter ${selectedId === player.number ? 'selected' : ''}`}
                    style={{ backgroundColor: tokenColor }}
                  >
                    <span className="num">{player.number}</span>
                    <span className="role">{tokenAbbrev}</span>
                  </div>
                  <span className="bench-item-name">{player.position.split(' (')[0]}</span>
                </div>
              );
            })}
        </div>
      </section>

      {/* Main Pitch Viewport */}
      <main ref={viewportRef} className={`pitch-viewport ${isBirdsEye ? 'birds-eye' : ''} ${isDesktop ? 'desktop-viewport-mode' : ''}`}>
        <div className="pitch-grid">
          {/* Grid Rows */}
          {ROWS.map((row) => {
            const rowLabel = row <= 13 ? 14 - row : row - 13;
            return (
              <React.Fragment key={row}>
                {/* Cells */}
                {COLUMNS.map((col, cIdx) => {
                  const colNum = cIdx + 1;
                  const placement = layout.find((c) => c.row === row && c.col === colNum);
                  const coverage = tackleZoneMap[`${row}-${col}`] || 0;

                  // Determine CSS classes for styling
                  const cellClasses = ['pitch-cell'];
                  if (pitchTheme === 'grass') {
                    if ((row + colNum) % 2 === 0) {
                      cellClasses.push('even-row-grass');
                    } else {
                      cellClasses.push('odd-row-grass');
                    }
                  } else {
                    if ((row + colNum) % 2 === 0) {
                      cellClasses.push('even-row-astro');
                    } else {
                      cellClasses.push('odd-row-astro');
                    }
                  }
                  if (col === 'E' || col === 'L') cellClasses.push('wide-border-left');
                  if (row === 13) cellClasses.push('scrimmage-line'); // Row 13 bottom is the chalk line
                  
                  // Alternating checkerboard end zones
                  if (row === 1) {
                    cellClasses.push('end-zone-opponent');
                  } else if (row === 26) {
                    cellClasses.push('end-zone-home');
                  }

                  // Tackle Zone coloring
                  if (coverage === 1) cellClasses.push('tackle-zone');
                  if (coverage > 1) cellClasses.push('tackle-zone-overlap');



                  // Two Diagonal Trapdoors: Opponent Side (Row 7, Column N) and Home Side (Row 20, Column B)
                  const isTrapdoor = (row === 7 && col === 'N') || (row === 20 && col === 'B');
                  // Two Sweet Spots: Row 7 & 20 on column H (8)
                  const isSweetSpot = (row === 7 || row === 20) && col === 'H';

                  const player = placement ? getPlayerByNum(placement.playerNumber) : null;
                  const archetype = player ? getArchetype(player.position) : 'Lineman';
                  const tokenColor = POSITION_COLORS[archetype] || POSITION_COLORS['Lineman'];
                  const tokenAbbrev = POSITION_ABBREVS[archetype] || 'LM';

                  return (
                    <div
                      key={`${row}-${col}`}
                      className={cellClasses.join(' ')}
                      onClick={() => handleCellTap(row, col)}
                    >
                      {/* Watermarks */}
                      {col === 'A' && rowLabel !== 13 && <span className="watermark-row-left">{rowLabel}</span>}
                      {col === 'O' && rowLabel !== 13 && <span className="watermark-row-right">{rowLabel}</span>}
                      {row === 1 && <span className="watermark-col-top">{col}</span>}
                      {row === 26 && <span className="watermark-col-bottom">{col}</span>}

                      {isTrapdoor && <div className="trapdoor-intersection-marker" title="Trapdoor" />}
                      {isSweetSpot && <div className="sweet-spot-crosshair" title="Sweet Spot" />}
                      {placement && player && (
                        <div
                          className={`player-counter ${selectedId === player.number ? 'selected' : ''}`}
                          style={{ backgroundColor: tokenColor }}
                        >
                          <span className="num">{player.number}</span>
                          <span className="role">{tokenAbbrev}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </main>
    </div>
  );
}
